import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { SIDE_EFFECT_CATALOG, SEVERITY_SORT_ORDER } from "@/lib/sideEffectsCatalog";
import { Activity } from "lucide-react";
import { ICON_MAP } from "@/components/patientDetail/constants";
import PatientInfoCard from "@/components/patientDetail/PatientInfoCard";
import SideEffectLogPanel from "@/components/patientDetail/SideEffectLogPanel";
import TreatmentCalendarsCard from "@/components/patientDetail/TreatmentCalendarsCard";
import AnalyticsTabPanel from "@/components/patientDetail/AnalyticsTabPanel";
import AllEffectsModal from "@/components/patientDetail/AllEffectsModal";

export default function PatientDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();

  // Core data
  const [patient, setPatient] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [effects, setEffects] = useState([]);

  // Side-effect form state
  const [newEffect, setNewEffect] = useState("");
  const [selectedEffectId, setSelectedEffectId] = useState("");
  const [frequency, setFrequency] = useState("");
  const [severity, setSeverity] = useState("");
  const [trend, setTrend] = useState("");

  // "All effects" modal state
  const [showAllEffects, setShowAllEffects] = useState(false);
  const [allSearch, setAllSearch] = useState("");
  const [allCategory, setAllCategory] = useState("all");
  const [allSortBy, setAllSortBy] = useState("recent");

  // Tab + analytics state
  const [view, setView] = useState("details"); // 'details' | 'analytics'
  const [analyticsData, setAnalyticsData] = useState(null);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);

  const load = async () => {
    const [p, c, e] = await Promise.all([
      api.get(`/patients/${id}`),
      api.get(`/calendars?patient_id=${id}`),
      api.get(`/patients/${id}/side-effects`),
    ]);
    setPatient(p.data); setCalendars(c.data); setEffects(e.data);
  };
  useEffect(() => { load(); }, [id]);

  // Lazy-load analytics only when that tab is opened.
  const fetchAnalytics = () => api.get(`/patients/${id}/analytics`).then((r) => {
    const d = r.data;
    const wc = (d.wellnessComponents || []).map((w) => ({ ...w, icon: ICON_MAP[w.iconName] || Activity }));
    setAnalyticsData({ ...d, wellnessComponents: wc });
  }).catch(() => setAnalyticsData(null));

  useEffect(() => { if (view === "analytics") fetchAnalytics(); }, [view, id]);

  const addEffect = async (e) => {
    e.preventDefault();
    if (!newEffect.trim() && !severity && !trend && !selectedEffectId) return;
    const catalogItem = selectedEffectId ? SIDE_EFFECT_CATALOG.find((x) => String(x.id) === String(selectedEffectId)) : null;
    await api.post(`/patients/${id}/side-effects`, {
      note: newEffect.slice(0, 500),
      reported_effects: [],
      severity: severity || (catalogItem ? catalogItem.severity : null),
      trend: trend || null,
      effect_id: catalogItem ? catalogItem.id : null,
      effect_name: catalogItem ? catalogItem.name : null,
      category: catalogItem ? catalogItem.category : null,
      frequency: frequency || (catalogItem ? catalogItem.frequency_hint : null),
      icon: catalogItem ? catalogItem.icon : null,
    });
    setNewEffect(""); setSelectedEffectId(""); setFrequency(""); setSeverity(""); setTrend("");
    const r = await api.get(`/patients/${id}/side-effects`);
    setEffects(r.data);
  };

  // Pre-fill severity/frequency when a catalog effect is picked
  const pickCatalogEffect = (effId) => {
    setSelectedEffectId(effId);
    const catalogItem = effId ? SIDE_EFFECT_CATALOG.find((x) => String(x.id) === String(effId)) : null;
    if (catalogItem) {
      setSeverity(catalogItem.severity);
      setFrequency(catalogItem.frequency_hint);
    }
  };

  // Filtering / sorting for the "All Reported Effects" modal
  const filteredEffects = effects.filter((e) => {
    const term = allSearch.trim().toLowerCase();
    const matchesSearch = !term ||
      (e.effect_name || "").toLowerCase().includes(term) ||
      (e.note || "").toLowerCase().includes(term) ||
      (e.category || "").toLowerCase().includes(term);
    const matchesCategory = allCategory === "all" || e.category === allCategory;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => {
    if (allSortBy === "severity") {
      const ra = SEVERITY_SORT_ORDER[a.severity] ?? 99;
      const rb = SEVERITY_SORT_ORDER[b.severity] ?? 99;
      if (ra !== rb) return ra - rb;
    }
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  if (!patient) return <div className="p-8 text-stone-500">Loading…</div>;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="patient-detail-page">
      <Link to="/patients" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 text-sm mb-4" data-testid="back-to-patients">
        <ArrowLeft className="w-4 h-4" />Back to patients
      </Link>

      <div className="mb-5 inline-flex bg-white border border-stone-200 rounded-xl p-1 shadow-soft" data-testid="patient-tabs">
        <button onClick={() => setView("details")} data-testid="tab-details" className={`px-4 py-2 rounded-lg text-sm font-semibold ${view === "details" ? "bg-brand-500 text-white" : "text-stone-700 hover:bg-stone-50"}`}>
          Patient details
        </button>
        <button onClick={() => setView("analytics")} data-testid="tab-analytics" className={`px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 ${view === "analytics" ? "bg-brand-500 text-white" : "text-stone-700 hover:bg-stone-50"}`}>
          <BarChart3 className="w-4 h-4" />ChemoAnalytics
        </button>
      </div>

      {view === "analytics" ? (
        <AnalyticsTabPanel
          patientId={id}
          analyticsData={analyticsData}
          showAssessmentModal={showAssessmentModal}
          onOpenAssessment={() => setShowAssessmentModal(true)}
          onCloseAssessment={() => setShowAssessmentModal(false)}
          onAssessmentSaved={() => { setShowAssessmentModal(false); fetchAnalytics(); }}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <PatientInfoCard patient={patient} />
            <SideEffectLogPanel
              effects={effects}
              newEffect={newEffect} setNewEffect={setNewEffect}
              selectedEffectId={selectedEffectId} pickCatalogEffect={pickCatalogEffect}
              severity={severity} setSeverity={setSeverity}
              trend={trend} setTrend={setTrend}
              frequency={frequency} setFrequency={setFrequency}
              onSubmit={addEffect}
              onOpenAll={() => setShowAllEffects(true)}
            />
          </div>
          <div className="lg:col-span-2">
            <TreatmentCalendarsCard
              calendars={calendars}
              onNew={() => nav(`/calendars/new?patient_id=${id}`)}
            />
          </div>
        </div>
      )}

      {showAllEffects && (
        <AllEffectsModal
          effects={effects}
          filteredEffects={filteredEffects}
          allSearch={allSearch} setAllSearch={setAllSearch}
          allCategory={allCategory} setAllCategory={setAllCategory}
          allSortBy={allSortBy} setAllSortBy={setAllSortBy}
          onClose={() => setShowAllEffects(false)}
        />
      )}
    </div>
  );
}
